import { useEffect, useState } from 'react';
import { adminApi, ensureArray } from '../services/api';

interface BackupSettings {
  scheduleEnabled: boolean;
  scheduleHourUtc: number;
  cadence: 'daily' | 'weekly';
  weekdayUtc: number;
  compress: boolean;
  encrypt: true;
  overwriteAfterDays: number;
  lastScheduledAt: string | null;
  storageReady: boolean;
  weekdays: string[];
}

interface BackupRun {
  id: string;
  status: 'running' | 'succeeded' | 'failed' | 'pruned';
  triggerMode: 'manual' | 'scheduled';
  compressed: boolean;
  encrypted: boolean;
  sizeBytes: number | null;
  errorMessage: string | null;
  startedAt: string;
  completedAt: string | null;
}

function formatSize(bytes: number | null): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function BackupsPage() {
  const [settings, setSettings] = useState<BackupSettings | null>(null);
  const [runs, setRuns] = useState<BackupRun[]>([]);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const load = () => {
    adminApi<{ settings: BackupSettings; runs: BackupRun[] }>('/admin/backups')
      .then((data) => {
        setSettings(data.settings);
        setRuns(ensureArray(data.runs));
      })
      .catch((err: unknown) => {
        setMessage(err instanceof Error ? err.message : 'Could not load backups.');
      });
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    setMessage('');
    try {
      const saved = await adminApi<{ settings: BackupSettings }>('/admin/backups/settings', {
        method: 'PUT',
        body: JSON.stringify({
          scheduleEnabled: settings.scheduleEnabled,
          scheduleHourUtc: settings.scheduleHourUtc,
          cadence: settings.cadence,
          weekdayUtc: settings.weekdayUtc,
          compress: settings.compress,
          overwriteAfterDays: settings.overwriteAfterDays,
        }),
      });
      setSettings(saved.settings);
      setMessage('Backup settings saved.');
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Could not save backup settings.');
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    setMessage('');
    try {
      const data = await adminApi<{ runs: BackupRun[] }>('/admin/backups/run', { method: 'POST' });
      setRuns(ensureArray(data.runs));
      setMessage('Backup stored.');
      load();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'The backup could not be completed.');
      load();
    } finally {
      setRunning(false);
    }
  };

  if (!settings) {
    return <p className="text-sm text-gray-500">Loading backups…</p>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Backups</h1>
        <p className="mt-1 text-sm text-gray-600">
          Database copies are compressed when you choose, encrypted, and stored in the backup
          bucket. Copies older than the overwrite period are removed after the next successful
          backup.
        </p>
      </div>

      {message ? <p className="text-sm text-gray-700">{message}</p> : null}
      {!settings.storageReady ? (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Set R2_BACKUP_BUCKET_NAME and R2_BACKUP_ENCRYPTION_KEY on the API and the worker before a
          backup can run.
        </p>
      ) : null}

      <form
        className="max-w-lg space-y-4 rounded-xl border border-gray-200 bg-white p-4 text-sm"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={settings.scheduleEnabled}
            onChange={(event) =>
              setSettings({ ...settings, scheduleEnabled: event.target.checked })
            }
          />
          Scheduled backup
        </label>

        <label className="block">
          How often
          <select
            className="mt-1 w-full rounded border px-2 py-1"
            value={settings.cadence}
            onChange={(event) =>
              setSettings({
                ...settings,
                cadence: event.target.value === 'weekly' ? 'weekly' : 'daily',
              })
            }
          >
            <option value="daily">Every day</option>
            <option value="weekly">Once a week</option>
          </select>
        </label>

        {settings.cadence === 'weekly' ? (
          <label className="block">
            Day (UTC)
            <select
              className="mt-1 w-full rounded border px-2 py-1"
              value={settings.weekdayUtc}
              onChange={(event) =>
                setSettings({ ...settings, weekdayUtc: Number(event.target.value) })
              }
            >
              {settings.weekdays.map((day, index) => (
                <option key={day} value={index}>
                  {day}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <label className="block">
          Hour (UTC)
          <input
            type="number"
            min={0}
            max={23}
            className="mt-1 w-24 rounded border px-2 py-1"
            value={settings.scheduleHourUtc}
            onChange={(event) =>
              setSettings({ ...settings, scheduleHourUtc: Number(event.target.value) })
            }
          />
        </label>

        <label className="flex items-center gap-2">
          <input type="checkbox" checked disabled />
          Encryption (always on)
        </label>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={settings.compress}
            onChange={(event) => setSettings({ ...settings, compress: event.target.checked })}
          />
          Compress
        </label>

        <label className="block">
          Overwrite period (days)
          <input
            type="number"
            min={1}
            max={365}
            className="mt-1 w-24 rounded border px-2 py-1"
            value={settings.overwriteAfterDays}
            onChange={(event) =>
              setSettings({ ...settings, overwriteAfterDays: Number(event.target.value) })
            }
          />
        </label>

        <p className="text-gray-500">
          Last scheduled run:{' '}
          {settings.lastScheduledAt ? new Date(settings.lastScheduledAt).toLocaleString() : '—'}
        </p>

        <div className="flex gap-2">
          <button
            type="submit"
            className="rounded-lg bg-gray-900 px-3 py-2 text-white"
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save settings'}
          </button>
          <button
            type="button"
            className="rounded-lg border px-3 py-2"
            disabled={running || !settings.storageReady}
            onClick={() => void runNow()}
          >
            {running ? 'Running…' : 'Run backup now'}
          </button>
        </div>
      </form>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b text-gray-500">
              <th className="py-2 pr-3">Started</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3">Trigger</th>
              <th className="py-2 pr-3">Stored size</th>
              <th className="py-2 pr-3">Compressed</th>
              <th className="py-2">Encrypted</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => (
              <tr key={run.id} className="border-b border-gray-100">
                <td className="py-2 pr-3">{new Date(run.startedAt).toLocaleString()}</td>
                <td className="py-2 pr-3">
                  {run.status}
                  {run.errorMessage ? (
                    <span className="mt-1 block text-xs text-red-700">{run.errorMessage}</span>
                  ) : null}
                </td>
                <td className="py-2 pr-3">{run.triggerMode}</td>
                <td className="py-2 pr-3">{formatSize(run.sizeBytes)}</td>
                <td className="py-2 pr-3">{run.compressed ? 'Yes' : 'No'}</td>
                <td className="py-2">{run.encrypted ? 'Yes' : 'No'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {runs.length === 0 ? <p className="mt-3 text-sm text-gray-500">No backups yet.</p> : null}
      </div>
    </div>
  );
}
